import {
  Directive,
  ElementRef,
  Input,
  HostListener,
  Renderer2,
  OnDestroy
} from '@angular/core';

@Directive({
    selector: '[appHoverMenu]',
    standalone: false
})
export class HoverMenuDirective implements OnDestroy {
  @Input('appHoverMenu') targetSelector!: string;
  @Input() hoverClass = 'active';
  @Input() delay = 300;

  private readonly viewportPadding = 8;
  private readonly menuSwitchDelay = 500;
  private isHovered = false;
  private hoverTimeoutId?: ReturnType<typeof setTimeout>;
  private positionTimeoutId?: ReturnType<typeof setTimeout>;

  constructor(private readonly el: ElementRef<HTMLElement>, private readonly renderer: Renderer2) {}

  @HostListener('mouseenter')
  onMouseEnter(): void {
    this.isHovered = true;
    const menuElement = this.findElement();
    this.scheduleHoverAction(() => {
      if (!menuElement || !this.isHovered) {
        return;
      }

      this.showMenu(menuElement);
    }, this.getMenuEnterDelay());
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    this.isHovered = false;
    this.clearPositionTimeout();
    const menuElement = this.findElement();
    this.scheduleHoverAction(() => {
      if (!menuElement) {
        return;
      }

      this.hideMenu(menuElement);
    });
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    const menuElement = this.findElement();

    if (!menuElement || !menuElement.classList.contains(this.hoverClass)) {
      return;
    }

    this.schedulePositionUpdate(menuElement);
  }

  private schedulePositionUpdate(menuElement: HTMLElement): void {
    this.clearPositionTimeout();
    this.positionTimeoutId = setTimeout(() => this.updateMenuPosition(menuElement));
  }

  private scheduleHoverAction(action: () => void, delayMs = this.delay): void {
    this.clearHoverTimeout();
    this.hoverTimeoutId = setTimeout(action, delayMs);
  }

  private showMenu(menuElement: HTMLElement): void {
    this.resetMenuPosition(menuElement);
    this.renderer.addClass(menuElement, this.hoverClass);
    this.schedulePositionUpdate(menuElement);
  }

  private hideMenu(menuElement: HTMLElement): void {
    this.renderer.removeClass(menuElement, this.hoverClass);
    this.resetMenuPosition(menuElement);
  }

  private updateMenuPosition(menuElement: HTMLElement): void {
    if (typeof window === 'undefined') {
      return;
    }

    this.resetMenuPosition(menuElement);

    const menuRect = menuElement.getBoundingClientRect();
    if (menuRect.height === 0) {
      return;
    }

    const minimumTop = this.viewportPadding;
    const maximumBottom = window.innerHeight - this.viewportPadding;
    const bottomOverflow = Math.max(0, menuRect.bottom - maximumBottom);
    const availableShiftUp = Math.max(0, menuRect.top - minimumTop);
    const shiftUp = Math.min(bottomOverflow, availableShiftUp);

    if (shiftUp === 0) {
      return;
    }

    this.renderer.setStyle(menuElement, 'top', `${-shiftUp}px`);
    this.renderer.setStyle(menuElement, 'bottom', 'auto');
  }

  private resetMenuPosition(menuElement: HTMLElement): void {
    this.renderer.removeStyle(menuElement, 'top');
    this.renderer.removeStyle(menuElement, 'bottom');
  }

  private findElement(): HTMLElement | null {
    if (!this.targetSelector) {
      return this.el.nativeElement;
    }
    return this.el.nativeElement.querySelector<HTMLElement>(this.targetSelector);
  }

  private getMenuEnterDelay(): number {
    if (!this.isPrimaryNavigationItem() || !this.hasActiveSiblingMenu()) {
      return this.delay;
    }

    return Math.max(this.delay, this.menuSwitchDelay);
  }

  private isPrimaryNavigationItem(): boolean {
    return this.el.nativeElement.parentElement?.classList.contains('primary-navigation') ?? false;
  }

  private hasActiveSiblingMenu(): boolean {
    const parentElement = this.el.nativeElement.parentElement;
    if (!parentElement || !this.targetSelector) {
      return false;
    }

    return Array.from(parentElement.children).some((childElement) => {
      if (!(childElement instanceof HTMLElement) || childElement === this.el.nativeElement) {
        return false;
      }

      const siblingMenu = childElement.querySelector<HTMLElement>(this.targetSelector);
      return siblingMenu?.classList.contains(this.hoverClass) ?? false;
    });
  }

  private clearHoverTimeout(): void {
    if (this.hoverTimeoutId) {
      clearTimeout(this.hoverTimeoutId);
      this.hoverTimeoutId = undefined;
    }
  }

  private clearPositionTimeout(): void {
    if (this.positionTimeoutId) {
      clearTimeout(this.positionTimeoutId);
      this.positionTimeoutId = undefined;
    }
  }

  ngOnDestroy(): void {
    this.clearHoverTimeout();
    this.clearPositionTimeout();
  }
}
